import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGS } from "../../content/catalog";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import LanguageMenu from "./LanguageMenu";

const LINKS = [
  { hash: "#hizmetler", key: "nav.services" },
  { hash: "#referanslar", key: "nav.references" },
  { hash: "#surec", key: "nav.process" },
  { hash: "#hakkimizda", key: "nav.about" },
];

function prefersReduced() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function NavBar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const current = (i18n.resolvedLanguage || i18n.language || "tr").slice(0, 2);

  const navigateTo = useCallback(
    (hash) => {
      const behavior = prefersReduced() ? "auto" : "smooth";
      if (!hash) {
        if (location.pathname !== "/") {
          navigate("/");
          return;
        }
        window.scrollTo({ top: 0, behavior });
        return;
      }
      if (location.pathname !== "/") {
        navigate(`/${hash}`);
        return;
      }
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior, block: "start" });
    },
    [location.pathname, navigate]
  );

  const go = (hash) => (event) => {
    event.preventDefault();
    if (open) {
      setOpen(false);
      // Menü kapanıp kaydırma kilidi kalkınca kaydır
      window.setTimeout(() => navigateTo(hash), 60);
      return;
    }
    navigateTo(hash);
  };

  // Mobil menü: kaydırmayı kilitle, Esc ile kapat, geniş ekrana geçince kapat
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth > 1000) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="site-nav" aria-label={t("nav.label")}>
        <a className="logo" href="/" onClick={go("")} aria-label="KODPİLOT">
          <Logo />
          <span>KOD<b>PİLOT</b></span>
          <img className="logo-img" src="/logo-white.png" alt="" width="551" height="72" decoding="async" />
        </a>
        <div className="nav-links">
          {LINKS.map((link) => (
            <a key={link.hash} href={`/${link.hash}`} onClick={go(link.hash)}>
              {t(link.key)}
            </a>
          ))}
        </div>
        <div className="nav-right">
          <LanguageMenu />
          <ThemeToggle />
          <a className="cta" href="/#iletisim" onClick={go("#iletisim")}>
            {t("nav.cta")} →
          </a>
        </div>
        <button
          type="button"
          className={`burger${open ? " is-open" : ""}`}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
          onClick={() => setOpen((value) => !value)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </nav>

      {open && (
        <div className="mobile-layer">
          <div className="mobile-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="mobile-menu" id="mobile-menu" role="dialog" aria-label={t("nav.label")}>
            <ul className="mm-links">
              {LINKS.map((link, index) => (
                <li key={link.hash} style={{ "--i": index }}>
                  <a href={`/${link.hash}`} onClick={go(link.hash)}>
                    <span className="mm-n">{String(index + 1).padStart(2, "0")}</span>
                    {t(link.key)}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mm-row">
              <div className="mm-langs" role="group" aria-label={t("footer.language")}>
                {LANGS.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    lang={lang.code}
                    aria-pressed={lang.code === current}
                    aria-label={lang.name}
                    onClick={() => i18n.changeLanguage(lang.code)}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
              <ThemeToggle />
            </div>

            <a className="mm-cta" href="/#iletisim" onClick={go("#iletisim")}>
              {t("nav.cta")} →
            </a>
          </div>
        </div>
      )}
    </>
  );
}
